import { useMemo, useState, useEffect } from "react";
import { Search, Wrench, Cog, Layers, ChevronRight, Phone, ChevronDown, ChevronUp } from "lucide-react";
import { SPARE_CATEGORIES, formatPrice } from "@/lib/spareParts";
import { useI18n } from "@/App";

const ICONS_BY_CAT = {
  SRDP_560: Cog,
  SRDP_780: Layers,
  HAMMER_MILL: Wrench,
};

const ACCENT = {
  SRDP_560: { bar: "from-emerald-500 to-emerald-700", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", tab: "bg-emerald-600" },
  SRDP_780: { bar: "from-amber-500 to-orange-600", chip: "bg-amber-50 text-amber-800 border-amber-200", tab: "bg-amber-500" },
  HAMMER_MILL: { bar: "from-sky-500 to-blue-600", chip: "bg-sky-50 text-sky-800 border-sky-200", tab: "bg-sky-600" },
};

export default function SparePartsSection({ onInquire }) {
  const { lang } = useI18n();
  const [activeCat, setActiveCat] = useState("SRDP_560");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  // Auto-expand when user clicks the "Spare Parts" nav link (#spare-parts)
  useEffect(() => {
    const openIfHash = () => {
      if (typeof window !== "undefined" && window.location.hash === "#spare-parts") {
        setExpanded(true);
      }
    };
    openIfHash();
    window.addEventListener("hashchange", openIfHash);
    return () => window.removeEventListener("hashchange", openIfHash);
  }, []);

  const active = SPARE_CATEGORIES.find((c) => c.key === activeCat);
  const Icon = ICONS_BY_CAT[activeCat];
  const totalCount = SPARE_CATEGORIES.reduce((a, c) => a + c.items.length, 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return active.items;
    return active.items.filter((it) => it.name.toLowerCase().includes(q));
  }, [active, query]);

  const inquire = (item) => {
    onInquire?.({
      id: item.id,
      name: item.name,
      spec: item.model,
      price: item.price,
      availability: lang === "HI" ? "स्टॉक में" : "Ready Stock",
    });
  };

  return (
    <section id="spare-parts" className="py-20 sm:py-24 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div className="max-w-2xl">
            <span className="bz-kicker">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <Cog className="w-3.5 h-3.5 text-slate-700" />
              {lang === "HI" ? "मशीन स्पेयर पार्ट्स" : "Machine Spare Parts"}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
              {lang === "HI" ? "पेलेट मशीन के ओरिजिनल स्पेयर पार्ट्स" : "Pellet Machine Spare Parts"}
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl">
              {lang === "HI"
                ? "SRDP 560, 780 और Hammer Mill (G2400/G3600/HMX/THM) के लिए Ready Stock स्पेयर पार्ट्स — बेहतरीन क्वालिटी और कम कीमत पर।"
                : "Ready-stock genuine spare parts for SRDP 560, SRDP 780 and Hammer Mill (G2400/G3600/HMX/THM). Industrial-grade quality at competitive pricing."}
            </p>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold text-emerald-800">
                {totalCount}+ {lang === "HI" ? "पार्ट्स स्टॉक में" : "parts in stock"}
              </span>
            </div>
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="spare-toggle"
              aria-expanded={expanded}
              className="inline-flex items-center gap-1.5 rounded-full text-white font-bold px-4 py-2 shadow-md"
              style={{ background: "#059669", boxShadow: "0 10px 24px -12px rgba(5,150,105,0.55)" }}
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  {lang === "HI" ? "छिपाएँ" : "Hide Parts"}
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  {lang === "HI" ? "पार्ट्स देखें" : "View Parts"}
                </>
              )}
            </button>
          </div>
        </div>

        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            data-testid="spare-expand-card"
            className="w-full text-left rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 sm:p-8 hover:border-emerald-400 transition group"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shrink-0">
                <Cog className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg sm:text-xl text-slate-900">
                  {lang === "HI"
                    ? `SRDP 560 / 780 और Hammer Mill के ${totalCount}+ पार्ट्स — सभी Ready Stock में`
                    : `${totalCount}+ parts across SRDP 560 / 780 & Hammer Mill — all Ready Stock`}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {lang === "HI"
                    ? "पूरा कैटलॉग देखने के लिए यहाँ क्लिक करें।"
                    : "Tap to open the full catalog with search & inquiry."}
                </p>
              </div>
              <ChevronDown className="w-6 h-6 text-emerald-700 group-hover:translate-y-0.5 transition" />
            </div>
          </button>
        ) : (
          <>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="spare-category-tabs">
          {SPARE_CATEGORIES.map((c) => {
            const isActive = activeCat === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setActiveCat(c.key)}
                data-testid={`spare-tab-${c.key.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  isActive
                    ? `${ACCENT[c.key].tab} text-white border-transparent shadow-md`
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {c.title.replace(" Spare Parts", "")}
                <span className="ml-2 text-xs opacity-80">{c.items.length}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="spare-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lang === "HI" ? "पार्ट का नाम खोजें…" : "Search part name…"}
            className="bz-input pl-10 bg-slate-50"
          />
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="spare-empty">
            {lang === "HI" ? "कोई पार्ट नहीं मिला।" : "No parts match your search."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={`spare-grid-${activeCat.toLowerCase()}`}>
            {filtered.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`spare-card-${item.id}`}
                className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 shadow-sm hover:shadow-lg transition"
              >
                <div className={`h-1 w-full bg-gradient-to-r ${ACCENT[activeCat].bar}`} />
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ACCENT[activeCat].bar} flex items-center justify-center shrink-0 shadow`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`inline-block text-[10px] tracking-widest font-bold border rounded-full px-2 py-0.5 mb-1.5 ${ACCENT[activeCat].chip}`}>
                        {item.model.toUpperCase()}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 leading-snug min-h-[2.5rem]">
                        {item.name}
                      </h3>
                    </div>
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                        {lang === "HI" ? "ऑफर प्राइस" : "Offer Price"}
                      </div>
                      <div className="font-display text-xl text-slate-900 leading-none mt-0.5">
                        {item.price}
                      </div>
                    </div>
                    <button
                      onClick={() => inquire(item)}
                      data-testid={`spare-inquire-${item.id}`}
                      className="inline-flex items-center gap-1 rounded-full text-white text-xs font-bold px-3.5 py-2 shadow"
                      style={{ background: "#059669" }}
                    >
                      {lang === "HI" ? "इंक्वायरी" : "Inquiry Now"}
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bulk help */}
        <div className="mt-10 rounded-2xl bg-slate-900 text-white p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-xl">
            <div className="text-xs tracking-widest uppercase text-emerald-400 font-semibold">
              {lang === "HI" ? "बल्क ऑर्डर / कस्टम कोटेशन" : "Bulk / Custom quotation"}
            </div>
            <div className="font-display text-xl sm:text-2xl mt-1">
              {lang === "HI"
                ? "बड़ा ऑर्डर या पूरे मशीन का सेट चाहिए?"
                : "Need a full set or bulk order?"}
            </div>
            <p className="text-slate-300 text-sm mt-1.5">
              {lang === "HI"
                ? "हमारी टीम आपको बेस्ट प्राइस और डिलीवरी टाइमलाइन देगी।"
                : "Our team will get you the best price and delivery timeline."}
            </p>
          </div>
          <a
            href="tel:+919812498875"
            data-testid="spare-call-cta"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-full px-5 py-3"
          >
            <Phone className="w-4 h-4" />
            {lang === "HI" ? "अभी कॉल करें" : "Call Us Now"}
          </a>
        </div>
          </>
        )}
      </div>
    </section>
  );
}
